import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import Wizard, { useWizard } from "../wizard/Wizard/Wizard";
import WizardStep, {
  CustomStepInjectedProps,
} from "../wizard/WizardStep/WizardStep";
import WorkflowBreadcrumbs from "../common/breadcrumbs/WorkflowBreadcrumbs";
import BreadcrumbItem from "../common/breadcrumbs/BreadcrumbItem";
import { LoadingState } from "../http";
import "./moveTeam.module.css";
import "../common/loader.module.css";
import MoveTeamInputStep from "./inputStep/MoveTeamInputStep";
import MoveTeamReviewStep from "./reviewStep/MoveTeamReviewStep";
import { MoveTeamContext } from "./MoveTeamContext";
import { MoveTeamWorkflowResult } from "./MoveTeamWorkflowResult";
import useWorkflowAuthorizationCheck, {
  UseWorkflowAuthorizationHookResult,
} from "../common/authorization/workflowAuthorization";
import Banner from "../banner/Banner";

const BREADCRUMBS_TITLE = "Move team";

const INPUT_STEP_TITLE = "Select new parent team";
const REVIEW_STEP_TITLE = "Review and submit";

const steps: (keyof MoveTeamWorkflowResult)[] = ["input", "review"];

interface MoveTeamWorkflowProps {
  inputStep?: React.FC<CustomStepInjectedProps<{ text: string }>>;
  reviewStep?: React.FC<CustomStepInjectedProps<undefined>>;
  workflowContext: MoveTeamContext;
}

const MoveTeamWorkflow: React.FC<MoveTeamWorkflowProps> = ({
  workflowContext,
}): JSX.Element => {
  const [result, setResult] = useWizard<MoveTeamWorkflowResult>({
    input: {},
  });

  const history = useHistory();
  const { metrics, teamId, breadcrumbs, displayBanner } = workflowContext;

  const {
    loadingState: isAuthorizedLoadingState,
    isAuthorized,
  }: UseWorkflowAuthorizationHookResult = useWorkflowAuthorizationCheck(
    teamId,
    metrics
  );

  useEffect(() => {
    if (
      isAuthorizedLoadingState === LoadingState.NO_DATA ||
      isAuthorizedLoadingState === LoadingState.LOADING
    ) {
      return;
    }
    console.log(`Authorization check request finished. AuthZ: ${isAuthorized}`);
    if (!isAuthorized) {
      throw new Error(
        "You are not an admin of this team. You must be a team admin to move a team."
      );
    }
  }, [isAuthorized, isAuthorizedLoadingState]);

  const breadcrumbItems: BreadcrumbItem[] = breadcrumbs.map(
    (ancestor: { name: string }) => ({ name: ancestor.name })
  );
  return (
    <div>
      <WorkflowBreadcrumbs
        breadcrumbItems={breadcrumbItems}
        workflowTitle={`${BREADCRUMBS_TITLE}`}
      />
      {displayBanner && <Banner />}
      <Wizard<MoveTeamWorkflowResult, MoveTeamContext>
        result={result}
        updateResult={setResult}
        steps={steps}
        workflowContext={workflowContext}
        onConfirmCancel={() => {
          history.push(`/team/${teamId}`);
        }}
      >
        <WizardStep<MoveTeamWorkflowResult, "input">
          stepId="input"
          stepTitle={`${INPUT_STEP_TITLE}`}
          component={MoveTeamInputStep}
          preventUnmount
        />
        <WizardStep<MoveTeamWorkflowResult, "review">
          stepId="review"
          stepTitle={`${REVIEW_STEP_TITLE}`}
          component={MoveTeamReviewStep}
        />
      </Wizard>
    </div>
  );
};

export default MoveTeamWorkflow;
